import { GAME_ASSET_SLUG } from "@/config/game";
import { cn } from "@/lib/utils";
import {
  RangeFilterType,
  SelectorFilterType,
  SelectorOption,
} from "@/pages/Players/FiltersPanel/filterTypes";
import { useTranslation } from "@repo/shared/i18n";
import { X } from "lucide-react";
import { useDispatch, useSelector } from "react-redux";

import { convertPlayerPrice } from "../../../../functions/convertPlayerPrice";
import { getVersionAssetsSelector } from "../../../../redux/loadedContent/loadedContentSlice";
import {
  activeFiltersSelector,
  clearAllFilters,
  clearFilter,
  clearFilterOption,
} from "../../../../redux/players/playersSlice";
import { AppDispatch } from "../../../../redux/store";
import { useFilterLabels } from "../../Filters/PlayerFilters/filterLabels";

export type ExcludedPlayStyleChips = {
  ids: number[];
  options: SelectorOption[];
  filterKey: "playStyles" | "playStylesPlus";
};

interface Props {
  activeFilters?: (RangeFilterType | SelectorFilterType)[];
  onClearFilter?: (filterKey: string) => void;
  onClearFilterOption?: (filterKey: string, option: number) => void;
  onClearAllFilters?: () => void;
  excludedPlayStyleChips?: ExcludedPlayStyleChips[];
  onClearExcludedPlayStyle?: (filterKey: string, id: number) => void;
  /** Overrides the clear-all chip's label, e.g. "Reset 2". */
  resetLabel?: string;
}

export const FiltersRow = ({
  activeFilters: activeFiltersProp,
  onClearFilter,
  onClearFilterOption,
  onClearAllFilters,
  excludedPlayStyleChips,
  onClearExcludedPlayStyle,
  resetLabel,
}: Props) => {
  const versionAssets = useSelector(getVersionAssetsSelector);
  const assetsBaseUrl = import.meta.env.VITE_APP_ASSETS_BASE_URL;
  const dispatch = useDispatch<AppDispatch>();
  const { t } = useTranslation(["players", "common"]);
  const { filterLabel, optionLabel } = useFilterLabels();

  // Use prop if provided, otherwise use selector (default behavior)
  const activeFiltersFromSelector = useSelector(activeFiltersSelector);
  const activeFilters = activeFiltersProp ?? activeFiltersFromSelector;

  // Default handlers dispatch to players slice
  const handleClearFilter = (filterKey: string) => {
    if (onClearFilter) {
      onClearFilter(filterKey);
    } else {
      dispatch(clearFilter(filterKey));
    }
  };

  const handleClearFilterOption = (filterKey: string, option: number) => {
    if (onClearFilterOption) {
      onClearFilterOption(filterKey, option);
    } else {
      dispatch(clearFilterOption({ filterKey, option }));
    }
  };

  const handleClearAllFilters = () => {
    if (onClearAllFilters) {
      onClearAllFilters();
    } else {
      dispatch(clearAllFilters());
    }
  };

  const imgFilters = [
    "country",
    "league",
    "club",
    "rarity",
    "playStyles",
    "playStylesPlus",
  ] as const;
  type ImgFilter = (typeof imgFilters)[number];

  const generateImgUrl = (id: number, type: ImgFilter) => {
    if (type === "country")
      return `${assetsBaseUrl}/${GAME_ASSET_SLUG}/countries/${id}.png`;
    else if (type === "league")
      return `${assetsBaseUrl}/${GAME_ASSET_SLUG}/leagues/dark/${id}.png`;
    else if (type === "club")
      return `${assetsBaseUrl}/${GAME_ASSET_SLUG}/clubs/dark/${id}.png`;
    else if (type === "rarity")
      return versionAssets[id]?.large[3]?.url || versionAssets[id]?.large?.url;
    else if (type === "playStyles")
      return `${assetsBaseUrl}/${GAME_ASSET_SLUG}/playstyles/${id}.png`;
    else if (type === "playStylesPlus")
      return `${assetsBaseUrl}/${GAME_ASSET_SLUG}/playstylesplus/${id}.png`;
  };

  const getFilterLabel = (
    filter: SelectorFilterType,
    selectedOption: number,
  ) => {
    const foundOption = filter.options.find((o) => o.id === selectedOption);
    const value = foundOption && optionLabel(filter.key, foundOption);

    if (filter.key === "skillMoves") {
      return t("filters.chip.skillMoves", { value });
    }
    if (filter.key === "weakFoot") {
      return t("filters.chip.weakFoot", { value });
    }
    if (filter.key === "preferredFoot") {
      return t("filters.chip.foot", { value });
    }
    return value || "";
  };

  const chip = ({
    key,
    label,
    img,
    onClick,
    tone,
  }: {
    key: string;
    label: string;
    img?: React.ReactNode;
    onClick: () => void;
    tone: "filter" | "excluded" | "reset";
  }) => (
    <button
      key={key}
      type="button"
      onClick={onClick}
      className={cn(
        "flex h-7 shrink-0 items-center gap-x-1.5 rounded-full border px-2.5 text-xs font-semibold whitespace-nowrap transition-colors",
        tone === "filter" &&
          "border-primary-border bg-blue-900/50 text-foreground hover:bg-blue-900/70",
        tone === "excluded" &&
          "border-red-700 bg-red-900/50 text-foreground hover:bg-red-900/70",
        tone === "reset" &&
          "border-transparent bg-control text-foreground-body hover:bg-neutral-600",
      )}
    >
      {img}
      {label}
      {tone !== "reset" && (
        <X
          className={cn(
            "size-3.5",
            tone === "filter" ? "text-blue-400" : "text-red-400",
          )}
        />
      )}
    </button>
  );

  const drawFilterRow = (filter: SelectorFilterType | RangeFilterType) => {
    if (filter.type === "SELECTOR_FILTER") {
      return (
        <div className="flex my-auto gap-x-1" key={filter.key}>
          {filter.selectedOptions.map((selectedOption) =>
            chip({
              key: `${filter.key}-${selectedOption}`,
              label: getFilterLabel(filter, selectedOption),
              onClick: () =>
                handleClearFilterOption(filter.key, selectedOption),
              tone: "filter",
              img: imgFilters.includes(filter.key as any) && (
                <img
                  className="w-4 my-auto"
                  src={generateImgUrl(selectedOption, filter.key as ImgFilter)}
                  alt={filterLabel(filter)}
                />
              ),
            }),
          )}
        </div>
      );
    }

    let minMaxStr = "";
    const min = filter.minValue && convertPlayerPrice(filter.minValue, true);
    const max =
      filter.maxValue !== null && filter.maxValue !== undefined
        ? convertPlayerPrice(filter.maxValue, true)
        : null;
    const label = filterLabel(filter);
    // Toggled on with the default range reads as a plain on/off filter
    if (
      filter.key === "offBalanceDribbling" &&
      filter.minValue === filter.range[0] &&
      filter.maxValue === null
    )
      minMaxStr = label;
    else if (min && max)
      minMaxStr = t("filters.chip.range", { label, min, max });
    else if (min) minMaxStr = t("filters.chip.min", { label, min });
    else if (max != null)
      minMaxStr = filter.key.startsWith("max")
        ? t("filters.chip.value", { label, max })
        : t("filters.chip.max", { label, max });

    return chip({
      key: filter.key,
      label: minMaxStr,
      onClick: () => handleClearFilter(filter.key),
      tone: "filter",
    });
  };

  return (
    <section className="no-scrollbar mr-auto flex max-w-full touch-manipulation flex-row gap-x-2 overflow-x-auto text-sm">
      {activeFilters.map((f) => drawFilterRow(f))}
      {excludedPlayStyleChips?.map((group) =>
        group.ids.map((id) => {
          const option = group.options.find((o) => o.id === id);
          if (!option) return null;
          const imgType = group.filterKey as ImgFilter;
          return chip({
            key: `excluded-${group.filterKey}-${id}`,
            label: option.label,
            onClick: () => onClearExcludedPlayStyle?.(group.filterKey, id),
            tone: "excluded",
            img: (
              <img
                className="w-4 my-auto"
                src={generateImgUrl(id, imgType)}
                alt={option.label}
              />
            ),
          });
        }),
      )}
      {chip({
        key: "clear-all",
        label: resetLabel ?? t("filters.panel.reset"),
        onClick: handleClearAllFilters,
        tone: "reset",
      })}
    </section>
  );
};
